/**
 * CoinGecko API Service
 *
 * Free API with no CORS restrictions - perfect for browser usage!
 * No API key required for basic usage
 */

const COINGECKO_BASE_URL = 'https://api.coingecko.com/api/v3'

// Cache duration: 2 minutes (CoinGecko updates frequently)
const CACHE_DURATION = 2 * 60 * 1000

interface CacheItem<T> {
  data: T
  timestamp: number
}

class CoinGeckoService {
  private cache: Map<string, CacheItem<any>> = new Map()

  private async fetchWithCache<T>(
    endpoint: string,
    cacheKey: string,
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`[CoinGecko] Using cached data for ${cacheKey}`)
      return cached.data
    }

    // Fetch from API
    console.log(`[CoinGecko] Fetching fresh data for ${cacheKey}`)

    const response = await fetch(`${COINGECKO_BASE_URL}${endpoint}`, {
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(
        `CoinGecko API Error: ${response.status} ${response.statusText}`,
      )
    }

    const data = await response.json()

    // Cache the result
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    })

    return data
  }

  /**
   * Fetch top cryptocurrencies by market cap
   */
  async getTopCryptocurrencies(limit: number = 100) {
    return this.fetchWithCache(
      `/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${limit}&page=1&sparkline=false&price_change_percentage=24h`,
      `crypto-markets-${limit}`,
    )
  }

  /**
   * Fetch cryptocurrency details by ID
   */
  async getCryptocurrencyDetails(id: string) {
    return this.fetchWithCache(
      `/coins/${id}?localization=false&tickers=false&community_data=false&developer_data=false`,
      `crypto-details-${id}`,
    )
  }

  /**
   * Fetch global market data
   */
  async getGlobalData() {
    return this.fetchWithCache('/global', 'global-data')
  }

  /**
   * Fetch trending cryptocurrencies
   */
  async getTrendingCoins() {
    return this.fetchWithCache('/search/trending', 'trending-coins')
  }

  /**
   * Search cryptocurrencies
   */
  async searchCoins(query: string) {
    return this.fetchWithCache(
      `/search?query=${encodeURIComponent(query)}`,
      `search-${query}`,
    )
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear()
    console.log('[CoinGecko] Cache cleared')
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(key: string) {
    this.cache.delete(key)
    console.log(`[CoinGecko] Cache entry cleared: ${key}`)
  }
}

// Export singleton instance
export const coinGeckoService = new CoinGeckoService()
